import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { XGrid } from '@material-ui/x-grid';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import { makeStyles } from '@material-ui/core/styles';
const ROW_SELECTOR = '.MuiDataGrid-row';

const useStyles = makeStyles(() => ({
  dragHandle: {
    cursor: 'grab',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    color: 'rgba(0, 0, 0, 0.54)',
    '&:active': {
      cursor: 'grabbing',
    },
  },
  '@global': {
    'body.rg-dragging, body.rg-dragging *': {
      cursor: 'grabbing !important',
    },
  },
}));
const reorderById = (list, draggedId, targetId) => {
  if (draggedId == null || targetId == null || String(draggedId) === String(targetId)) {
    return list;
  }
  const sourceIndex = list.findIndex(row => String(row.id) === String(draggedId));
  const targetIndex = list.findIndex(row => String(row.id) === String(targetId));
  if (sourceIndex === -1 || targetIndex === -1) return list;

  const next = [...list];
  const [moved] = next.splice(sourceIndex, 1);
  next.splice(targetIndex, 0, moved);
  return next;
};

const rowsEqual = (a, b) =>
  a.length === b.length && a.every((row, idx) => String(row.id) === String(b[idx]?.id));

const findRowElement = id => {
  if (id == null) return null;
  const selector = window.CSS && CSS.escape ? CSS.escape(String(id)) : String(id);
  try {
    return document.querySelector(`${ROW_SELECTOR}[data-id="${selector}"]`);
  } catch (e) {
    return null;
  }
};

const createDragGhost = (rowEl, clientX, clientY) => {
  const rect = rowEl.getBoundingClientRect();
  const ghost = rowEl.cloneNode(true);
  ghost.style.visibility = 'visible';
  ghost.style.display = '';
  const computedBg = window.getComputedStyle(rowEl).backgroundColor;
  ghost.style.backgroundColor =
    computedBg && computedBg !== 'rgba(0, 0, 0, 0)' ? computedBg : '#fff';

  ghost.style.position = 'fixed';
  ghost.style.transform = 'none';
  ghost.style.top = `${rect.top}px`;
  ghost.style.left = `${rect.left}px`;
  ghost.style.width = `${rect.width}px`;
  ghost.style.height = `${rect.height}px`;
  ghost.style.margin = 0;
  ghost.style.pointerEvents = 'none';
  ghost.style.zIndex = 9999;
  ghost.style.opacity = '0.85';
  ghost.style.filter = 'grayscale(15%)';
  ghost.style.boxShadow = '0 4px 14px rgba(0,0,0,0.3)';
  ghost.style.borderRadius = '4px';
  ghost.dataset.offsetX = clientX - rect.left;
  ghost.dataset.offsetY = clientY - rect.top;
  rowEl.parentNode.appendChild(ghost);
  const actualRect = ghost.getBoundingClientRect();
  const correctionX = rect.left - actualRect.left;
  const correctionY = rect.top - actualRect.top;
  ghost.dataset.correctionX = correctionX;
  ghost.dataset.correctionY = correctionY;
  ghost.style.left = `${rect.left + correctionX}px`;
  ghost.style.top = `${rect.top + correctionY}px`;

  return ghost;
};

const moveDragGhost = (ghost, clientX, clientY) => {
  if (!ghost) return;
  const offsetX = parseFloat(ghost.dataset.offsetX) || 0;
  const offsetY = parseFloat(ghost.dataset.offsetY) || 0;
  const correctionX = parseFloat(ghost.dataset.correctionX) || 0;
  const correctionY = parseFloat(ghost.dataset.correctionY) || 0;
  ghost.style.left = `${clientX - offsetX + correctionX}px`;
  ghost.style.top = `${clientY - offsetY + correctionY}px`;
};

const removeDragGhost = ghost => {
  if (ghost && ghost.parentNode) ghost.parentNode.removeChild(ghost);
};

function ReorderableGrid({ rows, columns, enableRowReordering, onRowsReordered, ...xgridProps }) {
  const classes = useStyles();
  const [localRows, setLocalRows] = useState(rows);
  const dragRef = useRef({
    localRows: rows,
    baselineRows: rows,
    dragging: false,
    draggedId: null,
    lastHoveredId: null,
    warned: false,
    listeners: null, // { onMouseMove, onMouseUp }
    draggedRowEl: null,
    ghostEl: null,
  });

  const enableRowReorderingRef = useRef(enableRowReordering);
  const onRowsReorderedRef = useRef(onRowsReordered);
  useEffect(() => {
    enableRowReorderingRef.current = enableRowReordering;
    onRowsReorderedRef.current = onRowsReordered;
  });
  useEffect(() => {
    if (!dragRef.current.dragging) {
      dragRef.current.localRows = rows;
      dragRef.current.baselineRows = rows;
      setLocalRows(rows);
    }
  }, [rows]);
  const cleanupDrag = useCallback(() => {
    const state = dragRef.current;
    if (state.listeners) {
      document.removeEventListener('mousemove', state.listeners.onMouseMove);
      document.removeEventListener('mouseup', state.listeners.onMouseUp);
      state.listeners = null;
    }
    document.body.style.userSelect = '';
    document.body.classList.remove('rg-dragging');
    removeDragGhost(state.ghostEl);
    state.ghostEl = null;
    if (state.draggedRowEl) {
      state.draggedRowEl.style.visibility = '';
      state.draggedRowEl = null;
    }
  }, []);

  useEffect(() => cleanupDrag, [cleanupDrag]);

  const resolveRowIdFromPoint = useCallback((clientX, clientY) => {
    const el = document.elementFromPoint(clientX, clientY);
    if (!el) return null;
    const rowEl = el.closest(`${ROW_SELECTOR}, [data-id], [data-rowindex], [role="row"]`);
    if (!rowEl) return null;

    const dataId = rowEl.getAttribute('data-id');
    if (dataId != null) return dataId;

    const rowIndexAttr = rowEl.getAttribute('data-rowindex');
    if (rowIndexAttr != null) {
      const idx = parseInt(rowIndexAttr, 10);
      const row = dragRef.current.localRows[idx];
      if (row) return String(row.id);
    }
    return null;
  }, []);

  const updateLocalRows = useCallback(updater => {
    setLocalRows(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      dragRef.current.localRows = next;
      return next;
    });
  }, []);

  const handleDragHandleMouseDown = useCallback(
    (event, row) => {
      if (!enableRowReorderingRef.current) return;
      event.preventDefault();
      event.stopPropagation();

      const state = dragRef.current;
      const id = row.id;

      state.dragging = true;
      state.draggedId = id;
      state.lastHoveredId = String(id);
      state.baselineRows = state.localRows;
      state.warned = false;

      document.body.style.userSelect = 'none';
      document.body.classList.add('rg-dragging');

      const rowEl = findRowElement(id);
      if (rowEl) {
        state.ghostEl = createDragGhost(rowEl, event.clientX, event.clientY);
        rowEl.style.visibility = 'hidden';
        state.draggedRowEl = rowEl;
      }

      const onMouseMove = moveEvent => {
        if (!state.dragging) return;

        moveDragGhost(state.ghostEl, moveEvent.clientX, moveEvent.clientY);

        const targetId = resolveRowIdFromPoint(moveEvent.clientX, moveEvent.clientY);
        if (targetId == null || targetId === state.lastHoveredId) return;
        state.lastHoveredId = targetId;
        updateLocalRows(prev => reorderById(prev, state.draggedId, targetId));
      };

      const onMouseUp = upEvent => {
        const finalTargetId = resolveRowIdFromPoint(upEvent.clientX, upEvent.clientY);
        const finalRows =
          finalTargetId != null
            ? reorderById(state.localRows, state.draggedId, finalTargetId)
            : state.localRows;

        cleanupDrag();
        updateLocalRows(finalRows);

        const baseline = state.baselineRows;
        state.dragging = false;
        state.draggedId = null;
        state.lastHoveredId = null;

        if (!rowsEqual(finalRows, baseline) && onRowsReorderedRef.current) {
          onRowsReorderedRef.current(finalRows);
        }
      };

      state.listeners = { onMouseMove, onMouseUp };
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [cleanupDrag, resolveRowIdFromPoint, updateLocalRows]
  );

  const reorderColumn = useMemo(
    () => ({
      field: ' ',
      headerName: '',
      width: 50,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'center',
      renderCell: params => (
        <span
          className={classes.dragHandle}
          draggable={false}
          onDragStart={e => e.preventDefault()}
          onMouseDown={e => handleDragHandleMouseDown(e, params.row)}
          onClick={e => e.stopPropagation()}
          title="Drag to reorder">
          <DragIndicatorIcon fontSize="small" />
        </span>
      ),
    }),
    [classes.dragHandle, handleDragHandleMouseDown]
  );

  const gridColumns = useMemo(
    () => (enableRowReordering ? [reorderColumn, ...columns] : columns),
    [enableRowReordering, columns, reorderColumn]
  );
  return (
    <XGrid {...xgridProps} rows={enableRowReordering ? localRows : rows} columns={gridColumns} />
  );
}

ReorderableGrid.propTypes = {
  rows: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  enableRowReordering: PropTypes.bool,
  onRowsReordered: PropTypes.func,
};

ReorderableGrid.defaultProps = {
  enableRowReordering: false,
  onRowsReordered: undefined,
};

export default ReorderableGrid;